import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const Mapping = {
  GMT0: { offset: 0, offsetZone: 0, description: 'GMT + 00:00' },
  GMT0100: { offset: 60, offsetZone: 1, description: 'GMT + 01:00' },
  GMT0200: { offset: 120, offsetZone: 2, description: 'GMT + 02:00' },
  GMT0300: { offset: 180, offsetZone: 3, description: 'GMT + 03:00' },
  GMT0400: { offset: 240, offsetZone: 4, description: 'GMT + 04:00' },
  GMT0500: { offset: 300, offsetZone: 5, description: 'GMT + 05:00' },
  GMT0530: { offset: 330, offsetZone: 5.5, description: 'GMT + 05:30' },
  GMT0600: { offset: 360, offsetZone: 6, description: 'GMT + 06:00' },
  GMT0700: { offset: 420, offsetZone: 7, description: 'GMT + 07:00' },
  GMT0800: { offset: 480, offsetZone: 8, description: 'GMT + 08:00' },
  GMT0900: { offset: 540, offsetZone: 9, description: 'GMT + 09:00' },
  GMT0930: { offset: 570, offsetZone: 9.5, description: 'GMT + 09:30' },
  GMT1000: { offset: 600, offsetZone: 10, description: 'GMT + 10:00' },
  GMT1100: { offset: 660, offsetZone: 11, description: 'GMT + 11:00' },
  GMT1200: { offset: 720, offsetZone: 12, description: 'GMT + 12:00' },
  GMT1300: { offset: 780, offsetZone: 13, description: 'GMT + 13:00' },
  GMT1400: { offset: 840, offsetZone: 14, description: 'GMT + 14:00' },
  GMT_0100: { offset: -60, offsetZone: -1, description: 'GMT - 01:00' },
  GMT_0200: { offset: -120, offsetZone: -2, description: 'GMT - 02:00' },
  GMT_0300: { offset: -180, offsetZone: -3, description: 'GMT - 03:00' },
  GMT_0400: { offset: -240, offsetZone: -4, description: 'GMT - 04:00' },
  GMT_0500: { offset: -300, offsetZone: -5, description: 'GMT - 05:00' },
  GMT_0600: { offset: -360, offsetZone: -6, description: 'GMT - 06:00' },
  GMT_0700: { offset: -420, offsetZone: -7, description: 'GMT - 07:00' },
  GMT_0800: { offset: -480, offsetZone: -8, description: 'GMT - 08:00' },
  GMT_0900: { offset: -540, offsetZone: -9, description: 'GMT - 09:00' },
  GMT_1000: { offset: -600, offsetZone: -10, description: 'GMT - 10:00' },
  GMT_1100: { offset: -660, offsetZone: -11, description: 'GMT - 11:00' },
  GMT_1200: { offset: -720, offsetZone: -12, description: 'GMT - 12:00' },
};

const handleDigit = utcTimeStrOrObj => {
  const splits = utcTimeStrOrObj.split(':');
  let hour = 0;
  let minute = 0;
  let second = 0;
  let ms = 0;
  if (splits.length >= 1) {
    hour = parseInt(splits[0].trim(), 10);
    if (splits.length >= 2) {
      minute = parseInt(splits[1].trim(), 10);
      if (splits.length >= 3) {
        second = parseInt(splits[2].trim(), 10);
        if (splits.length >= 4) {
          ms = parseInt(splits[3].trim(), 10);
        }
      }
    }
  }
  return hour + minute / 60 + second / 3600 + ms / 3600000;
};
//Must be 13:00 like, or 13:00:00:000 for ms
const timeStrToNumber = utcTimeStrOrObj => {
  if (utcTimeStrOrObj === '' || !utcTimeStrOrObj) {
    return 0;
  }
  const indexSplit = utcTimeStrOrObj.indexOf(':');
  if (indexSplit < 0) {
    const time = parseInt(utcTimeStrOrObj, 10);
    if (!isNaN(time)) {
      return time;
    }
  } else if (indexSplit === 0) {
    const time = parseInt(utcTimeStrOrObj.slice(1), 10);
    if (!isNaN(time)) {
      return time;
    }
  } else {
    return handleDigit(utcTimeStrOrObj);
  }
  return 0;
};

export const DateManager = {
  formatDate(dateInput, format) {
    return dayjs(dateInput).format(format || 'YYYY-MM-DD');
  },
  format(dateInput, dateOrTimeFormat = 'YYYY-MM-DD') {
    return dayjs().format(dateInput, dateOrTimeFormat);
  },
  fromNow(from, format) {
    return dayjs(from, format).fromNow();
  },
  timeStrToNumber,
  // utcTimeStrOrObj = 13:00 for example,
  timeFromUtcToZone(utcTimeStrOrObj, timeZoneStr) {
    const hours = timeStrToNumber(utcTimeStrOrObj);
    const timeZoneObj = Mapping[timeZoneStr] || Mapping.GMT0;
    return Math.abs(24 + hours + parseFloat(timeZoneObj.offsetZone, 10)) % 24;
  },
  timeFromZoneToUtc(utcTimeStrOrObj, timeZoneStr) {
    const hours = timeStrToNumber(utcTimeStrOrObj);
    const timeZoneObj = Mapping[timeZoneStr] || Mapping.GMT0;
    return Math.abs(24 + hours - parseFloat(timeZoneObj.offsetZone, 10)) % 24;
  },
  fromUtcToZone(utcDateStrOrObj, timeZoneStr, dateOrTimeformat) {
    if (utcDateStrOrObj === '' || !utcDateStrOrObj) {
      return '';
    }
    const timeZoneObj = Mapping[timeZoneStr] || Mapping.GMT0;
    return dayjs.utc(utcDateStrOrObj).utcOffset(timeZoneObj.offsetZone).format(dateOrTimeformat);
  },
  fromZoneToUtc(
    zoneDateStrOrObj,
    timeZoneStr,
    dateOrTimeformat = 'MM/DD/YYYYTHH:mm:ss',
    utcDateOrTimeformat = 'YYYY-MM-DDTHH:mm:ss',
  ) {
    const timeZoneObj = Mapping[timeZoneStr] || Mapping.GMT0;
    return dayjs(zoneDateStrOrObj, dateOrTimeformat)
      .add(-timeZoneObj.offsetZone, 'hours')
      .format(utcDateOrTimeformat);
  },
  fromUtcToDst(utcDateStrOrObj, timeZoneStr, dateOrTimeformat) {
    //timeZoneStr for example "America/Los_Angeles"
    //refer https://momentjs.com/timezone/docs/
    return dayjs.utc(utcDateStrOrObj).tz(timeZoneStr).format(dateOrTimeformat);
  },
  formDstToUtc(
    zoneDateStrOrObj,
    timeZoneStr,
    dateOrTimeformat = 'MM/DD/YYYYTHH:mm:ss',
    utcDateOrTimeformat = 'YYYY-MM-DDTHH:mm:ss',
  ) {
    //timeZoneStr for example "America/Los_Angeles"
    //refer https://momentjs.com/timezone/docs/
    return dayjs
      .tz(zoneDateStrOrObj, dateOrTimeformat, timeZoneStr)
      .tz('Etc/GMT')
      .format(utcDateOrTimeformat);
  },
  UtcMapping: Mapping,
  GmtMapping: Mapping,
  moment: dayjs,
};
